import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import googleDriveAxios from "../../common/googleDriveAxios";

interface Checklist {
  title: string | null;
  notes: string | null;
  status: string | null;
}

const RoughIn: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  canAutoSetChassisNo: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, canAutoSetChassisNo }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [specAndVisualPlanChecklist, setSpecAndVisualPlanChecklist] = useState<
    Checklist[]
  >([]);
  const [vCableChecklist, setVCableChecklist] = useState<Checklist[]>([]);
  const [vPowerChecklist, setVPowerChecklist] = useState<Checklist[]>([]);
  const [externalChecklist, setExternalChecklist] = useState<Checklist[]>([]);
  const [inverterChecklist, setInverterChecklist] = useState<Checklist[]>([]);
  const [otherChecklist, setOtherChecklist] = useState<Checklist[]>([]);
  const [othersChecklist, setOthersChecklist] = useState<Checklist[]>([]);
  const [specAndVisualVisualChecklist, setSpecAndVisualVisualChecklist] =
    useState<Checklist[]>([]);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "electrical-fit-off",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  const allChecklists = useMemo(
    () => [
      ...specAndVisualPlanChecklist,
      ...vCableChecklist,
      ...vPowerChecklist,
      ...externalChecklist,
      ...inverterChecklist,
      ...otherChecklist,
      ...othersChecklist,
      ...specAndVisualVisualChecklist,
    ],
    [
      specAndVisualPlanChecklist,
      vCableChecklist,
      vPowerChecklist,
      externalChecklist,
      inverterChecklist,
      otherChecklist,
      othersChecklist,
      specAndVisualVisualChecklist,
    ]
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
        setSpecAndVisualPlanChecklist([]);
        setVCableChecklist([]);
        setVPowerChecklist([]);
        setExternalChecklist([]);
        setInverterChecklist([]);
        setOtherChecklist([]);
        setOthersChecklist([]);
        setSpecAndVisualVisualChecklist([]);
        setApiError(false);
        setModel("");
        setCompleteStatus("Incomplete");
        return;
      }

      setLoading(true);
      setApiError(false);
      setModel("");
      try {
        const response = await axiosPrivate.post(
          `/prepare_electrical_fit_off/${chassisNo}`
        );
        const data = response.data;

        const specPlanData = (data.specAndVisualPlanChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const vCableData = (data.vCableChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const vPowerData = (data.vPowerChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const externalData = (data.externalChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const inverterData = (data.inverterChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const otherData = (data.otherChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const othersData = (data.othersChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const specVisualData = (
          data.specAndVisualVisualChecklistItems || []
        ).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));

        setSpecAndVisualPlanChecklist(specPlanData);
        setVCableChecklist(vCableData);
        setVPowerChecklist(vPowerData);
        setExternalChecklist(externalData);
        setInverterChecklist(inverterData);
        setOtherChecklist(otherData);
        setOthersChecklist(othersData);
        setSpecAndVisualVisualChecklist(specVisualData);
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");
        setModel(data.model || "");

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);

        const allItems = [
          ...specPlanData,
          ...vCableData,
          ...vPowerData,
          ...externalData,
          ...inverterData,
          ...otherData,
          ...othersData,
          ...specVisualData,
        ];
        const isComplete =
          allItems.length > 0 &&
          allItems.every(
            (item) =>
              item.status &&
              ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
        console.log(
          "fetchData - isComplete:",
          isComplete,
          "completeStatus:",
          isComplete ? "Complete" : "Incomplete",
          "loading:",
          false
        );
      } catch (error: any) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 500) {
          setApiError(true);
        } else {
          toast.error("Failed to fetch data.");
        }
      } finally {
        setLoading(false);
        console.log("fetchData - loading reset to:", false);
      }
    };

    fetchData();
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    console.log("saveData - loading set to:", true);
    try {
      const allChecklists = [
        ...specAndVisualPlanChecklist,
        ...vCableChecklist,
        ...vPowerChecklist,
        ...externalChecklist,
        ...inverterChecklist,
        ...otherChecklist,
        ...othersChecklist,
        ...specAndVisualVisualChecklist,
      ];

      const isComplete =
        allChecklists.length > 0 &&
        allChecklists.every(
          (item) =>
            item.status &&
            ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
        );
      const newCompleteStatus = isComplete ? "Complete" : "Incomplete";
      setCompleteStatus(newCompleteStatus);
      console.log(
        "saveData - isComplete:",
        isComplete,
        "completeStatus:",
        newCompleteStatus
      );

      const payload = {
        chassisNo,
        model,
        specAndVisualPlanChecklistItems: specAndVisualPlanChecklist,
        vCableChecklistItems: vCableChecklist,
        vPowerChecklistItems: vPowerChecklist,
        externalChecklistItems: externalChecklist,
        inverterChecklistItems: inverterChecklist,
        otherChecklistItems: otherChecklist,
        othersChecklistItems: othersChecklist,
        specAndVisualVisualChecklistItems: specAndVisualVisualChecklist,
        checkedBy,
        createdOn: date,
        completeStatus: newCompleteStatus,
      };

      await toast.promise(
        axiosPrivate.post("/create_or_update_electrical_fit_off", payload),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      try {
        const response = await axiosPrivate.post(
          `/prepare_electrical_fit_off/${chassisNo}`
        );
        const data = response.data;

        setSpecAndVisualPlanChecklist(
          (data.specAndVisualPlanChecklistItems || []).map(
            (item: Checklist) => ({
              ...item,
              status: item.status
                ? item.status.trim().toLowerCase()
                : "not checked",
            })
          )
        );
        setVCableChecklist(
          (data.vCableChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setVPowerChecklist(
          (data.vPowerChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setExternalChecklist(
          (data.externalChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setInverterChecklist(
          (data.inverterChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setOtherChecklist(
          (data.otherChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setOthersChecklist(
          (data.othersChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setSpecAndVisualVisualChecklist(
          (data.specAndVisualVisualChecklistItems || []).map(
            (item: Checklist) => ({
              ...item,
              status: item.status
                ? item.status.trim().toLowerCase()
                : "not checked",
            })
          )
        );
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");
        setModel(data.model || "");

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);
      } catch (error) {
        console.error("Error re-fetching data after save:", error);
        toast.error("Failed to sync data with server.");
      }

      if (checkedBy) {
        const allChecklists = [
          ...specAndVisualPlanChecklist,
          ...vCableChecklist,
          ...vPowerChecklist,
          ...externalChecklist,
          ...inverterChecklist,
          ...otherChecklist,
          ...othersChecklist,
          ...specAndVisualVisualChecklist,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter((part) => part);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${notesStr}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "electrical-fit-off",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "electrical-fit-off" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
      console.log(
        "saveData - loading reset to:",
        false,
        "completeStatus:",
        completeStatus
      );
    }
  };

  const handleNotesChange = (index: number, value: string, type: string) => {
    if (canAutoSetChassisNo && completeStatus === "Complete") {
      console.log(
        "Notes change blocked: canAutoSetChassisNo=",
        canAutoSetChassisNo,
        "completeStatus=",
        completeStatus
      );
      return;
    }

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].notes = value;
      setChecklist(updatedData);
    };

    switch (type) {
      case "specPlan":
        updateChecklist(
          specAndVisualPlanChecklist,
          setSpecAndVisualPlanChecklist
        );
        break;
      case "vCable":
        updateChecklist(vCableChecklist, setVCableChecklist);
        break;
      case "vPower":
        updateChecklist(vPowerChecklist, setVPowerChecklist);
        break;
      case "external":
        updateChecklist(externalChecklist, setExternalChecklist);
        break;
      case "inverter":
        updateChecklist(inverterChecklist, setInverterChecklist);
        break;
      case "other":
        updateChecklist(otherChecklist, setOtherChecklist);
        break;
      case "others":
        updateChecklist(othersChecklist, setOthersChecklist);
        break;
      case "specAndVisualVisual":
        updateChecklist(
          specAndVisualVisualChecklist,
          setSpecAndVisualVisualChecklist
        );
        break;
    }
  };

  const handleRadioChange = (index: number, value: string, type: string) => {
    if (canAutoSetChassisNo && completeStatus === "Complete") {
      console.log(
        "Radio change blocked: canAutoSetChassisNo=",
        canAutoSetChassisNo,
        "completeStatus=",
        completeStatus
      );
      return;
    }

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    switch (type) {
      case "specPlan":
        updateChecklist(
          specAndVisualPlanChecklist,
          setSpecAndVisualPlanChecklist
        );
        break;
      case "vCable":
        updateChecklist(vCableChecklist, setVCableChecklist);
        break;
      case "vPower":
        updateChecklist(vPowerChecklist, setVPowerChecklist);
        break;
      case "external":
        updateChecklist(externalChecklist, setExternalChecklist);
        break;
      case "inverter":
        updateChecklist(inverterChecklist, setInverterChecklist);
        break;
      case "other":
        updateChecklist(otherChecklist, setOtherChecklist);
        break;
      case "others":
        updateChecklist(othersChecklist, setOthersChecklist);
        break;
      case "specAndVisualVisual":
        updateChecklist(
          specAndVisualVisualChecklist,
          setSpecAndVisualVisualChecklist
        );
        break;
    }
  };

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  const checklistsWithSerialNumbers = [
    {
      data: specAndVisualPlanChecklist,
      type: "specPlan",
      subHeaderText: "Specifications & Visual Inspection (Plan)",
    },
    { data: vCableChecklist, type: "vCable", subHeaderText: "240V Cable" },
    {
      data: vPowerChecklist,
      type: "vPower",
      subHeaderText: "12 V (Figure 8 Wires) - Power",
    },
    { data: externalChecklist, type: "external", subHeaderText: "External" },
    { data: inverterChecklist, type: "inverter", subHeaderText: "Inverter" },
    { data: otherChecklist, type: "other", subHeaderText: "Other" },
    { data: othersChecklist, type: "others", subHeaderText: "Others" },
    {
      data: specAndVisualVisualChecklist,
      type: "specAndVisualVisual",
      subHeaderText: "Specifications & Visual Inspection (Visual)",
    },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    sx={{
                      position: "absolute",
                      top: "60%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  console.log(
                    `Rendering ${checklistSection.type} with serialNumberStart: ${serialNumberStart}, item count: ${checklistSection.data.length}`
                  );

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={completeStatus === "Complete"}
                      allNotChecked={allNotChecked}
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"electrical-fit-off"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (canAutoSetChassisNo && completeStatus === "Complete") {
                console.log(
                  "Checked By change blocked: canAutoSetChassisNo=",
                  canAutoSetChassisNo,
                  "completeStatus=",
                  completeStatus
                );
                return;
              }
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") {
                console.log(
                  "Date change blocked: canAutoSetChassisNo=",
                  canAutoSetChassisNo,
                  "completeStatus=",
                  completeStatus
                );
                return;
              }
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default RoughIn;
